import { throttle } from 'lodash';
import Image from 'next/image';
import { useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import Divider from '@/components/atom/Divider';
import FavoriteStar from '@/components/atom/icon/FavoriteStar';
import IconLocationPinMono from '@/components/atom/icon/IconLocationPinMono';
import Phone from '@/components/atom/icon/Phone';
import RightArrowCircle from '@/components/atom/icon/f2f/RightArrowCircle';
import SmallRightArrowCircle from '@/components/atom/icon/f2f/SmallRightArrowCircle';
import DiagnosisStatus from '@/components/hospital/HospitalListItem/DiagnosisStatus';
import theme from '@/styles/theme';
import { DiagnosisState } from '@/types/hospital';
import { HospitalAdBannerVo } from '@/types/hospital';

export interface HospitalListItemProps {
  status: DiagnosisState;
  name: string;
  rating: string;
  reviewTotalCount: number;
  address: string;
  badges: string[];
  imageUrl?: string | null;
  isBookable?: boolean;
  representativeDepartmentName?: string;
  isImpressionLog?: boolean;
  hospitalPid: string;
  operationDisplay?: string;
  adBanners?: HospitalAdBannerVo[];
  onClickAdBanner?: (url: string, hospitalPid: string) => void;
  hasNonF2fDoctors?: boolean;
  isLastItem?: boolean;
}

class BadgeItem {
  constructor(
    readonly type: BadgeType,
    readonly label: string
  ) {}
}

enum BadgeType {
  Normal = 'Normal',
  NonF2fDoctor = 'NonF2fDoctor',
}

const NeighborhoodSearchHospitalItem = ({
  imageUrl,
  status,
  name,
  rating,
  address,
  badges,
  reviewTotalCount,
  isBookable = false,
  representativeDepartmentName,
  operationDisplay,
  adBanners,
  hospitalPid,
  onClickAdBanner,
  hasNonF2fDoctors,
  isLastItem,
}: HospitalListItemProps) => {
  const visibleBadgeList = useMemo(() => {
    if (hasNonF2fDoctors) {
      return [
        new BadgeItem(BadgeType.NonF2fDoctor, '비대면진료'),
        ...badges.slice(0, 2).map((badge) => new BadgeItem(BadgeType.Normal, badge)),
      ];
    }
    return badges.slice(0, 3).map((badge) => new BadgeItem(BadgeType.Normal, badge));
  }, [badges, hasNonF2fDoctors]);
  const hasMoreBadge = badges.length > visibleBadgeList.length;
  const moreBadgeLength = badges.length - visibleBadgeList.length;

  const [moreButtonVisible, setMoreButtonVisible] = useState<boolean>(false);
  const adBannerScrollRef = useRef<HTMLDivElement>(null);

  const checkIsScrollEnd = (scrollWidth: number, offsetWidth: number, scrollLeft: number) => {
    const contentWidth = scrollWidth - offsetWidth;
    const isEnd = contentWidth - scrollLeft <= 10;
    return isEnd;
  };

  const onAdBannerScrollEnd = throttle((event) => {
    const { scrollWidth, offsetWidth, scrollLeft } = event.target;
    const isEnd = checkIsScrollEnd(scrollWidth, offsetWidth, scrollLeft);

    setMoreButtonVisible(isEnd ? false : true);
  });

  const onAdBannerMouseEnter = throttle(() => {
    const adBannerScroll = adBannerScrollRef?.current;
    if (!adBannerScroll) return;

    const { scrollWidth, offsetWidth, scrollLeft } = adBannerScroll;
    const isEnd = checkIsScrollEnd(scrollWidth, offsetWidth, scrollLeft);

    if (!isEnd) {
      setMoreButtonVisible(true);
    }
  });

  useEffect(() => {
    adBannerScrollRef?.current?.addEventListener('scroll', onAdBannerScrollEnd);
    return () => {
      adBannerScrollRef?.current?.removeEventListener('scroll', onAdBannerScrollEnd);
    };
  }, []);

  return (
    <>
      <Wrapper>
        <TopWrapper>
          <HospitalWrapper>
            <NameWrapper>
              <h3>{name}</h3>
            </NameWrapper>
            {representativeDepartmentName && (
              <RepresentativeDepartment>
                <DepartmentName>{representativeDepartmentName}</DepartmentName>
              </RepresentativeDepartment>
            )}
            {status !== DiagnosisState.Unknown && (
              <DiagnosisTimeWrapper>
                <DiagnosisStatus type={status} />
                {!!operationDisplay && (
                  <>
                    <Stick />
                    <OperationDisplayTime>{operationDisplay}</OperationDisplayTime>
                  </>
                )}
              </DiagnosisTimeWrapper>
            )}
            <IconInfoWrapper>
              {reviewTotalCount > 0 && (
                <InfoWrapper>
                  <FavoriteStar size={14} fullColor={theme.color.yellow500} />
                  <Score>{rating}</Score>
                  <ReviewCount>({reviewTotalCount})</ReviewCount>
                </InfoWrapper>
              )}

              <DistanceWrapper>
                <IconLocationPinMono fill={theme.color.gray300} />
                <Address>{address}</Address>
              </DistanceWrapper>
            </IconInfoWrapper>
          </HospitalWrapper>
          {imageUrl && (
            <ImageWrapper>
              <HospitalImage src={imageUrl} width={56} height={56} alt={name} loading='lazy' />
            </ImageWrapper>
          )}
        </TopWrapper>

        <BadgeWrapper hasItem={badges.length > 0}>
          {visibleBadgeList.map((badge, i) => {
            switch (badge.type) {
              case BadgeType.Normal:
                return <Badge key={`badge-${name}-${badge}-${i}`}>{badge.label}</Badge>;
              case BadgeType.NonF2fDoctor:
                return (
                  <NonF2fBadge key={`badge-${name}-${badge}-${i}`}>
                    <Phone />
                    {badge.label}
                  </NonF2fBadge>
                );
            }
          })}
          {hasMoreBadge && <Badge key={`badge-${name}-+`}>+{moreBadgeLength}</Badge>}
        </BadgeWrapper>
        {!!adBanners?.length && (
          <AdBanners
            ref={adBannerScrollRef}
            onMouseEnter={onAdBannerMouseEnter}
            onMouseLeave={() => {
              setMoreButtonVisible(false);
            }}
          >
            {adBanners.map((item, index) => {
              if (index > 2) return null;
              const isSingle = adBanners.length === 1;
              return (
                <AdBanner key={item.id} isSingle={isSingle} onClick={() => onClickAdBanner?.(item.url, hospitalPid)}>
                  <Image src={item.thumbnailImageUrl} width={56} height={56} alt={item.title} loading='lazy' />
                  <AdContent>
                    <AdContentTitle>{item.title}</AdContentTitle>
                    <PriceWrapper>
                      <DiscountPercent>{item.displayDiscount}</DiscountPercent>
                      <Price>{item.displayPrice}</Price>
                    </PriceWrapper>
                  </AdContent>
                </AdBanner>
              );
            })}
            {adBanners.length > 3 && (
              <MoreWrapper>
                <SmallRightArrowCircle />
                <MoreText>더 보기</MoreText>
              </MoreWrapper>
            )}
            <HoveredMoreIcon
              onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                e.preventDefault();

                adBannerScrollRef.current?.scrollBy({
                  left:
                    adBannerScrollRef.current.offsetLeft +
                    (adBannerScrollRef.current.firstElementChild?.getBoundingClientRect().width ?? 0) +
                    12,
                  behavior: 'smooth',
                });
              }}
              isHovered={moreButtonVisible}
            >
              <RightArrowCircle />
            </HoveredMoreIcon>
          </AdBanners>
        )}
      </Wrapper>
      {!isLastItem && (
        <DividerWrapper>
          <InnerDivider height={'1px'} color={theme.color.gray40} />
        </DividerWrapper>
      )}
    </>
  );
};

export default NeighborhoodSearchHospitalItem;

const Wrapper = styled.li`
  position: relative;
  padding: 20px 0;
  display: flex;
  flex-direction: column;
`;

const ImageWrapper = styled.div`
  position: relative;
  max-width: 56px;
  max-height: 56px;
  border-radius: 8px;

  width: 100%;
  height: 100%;
  margin-left: 20px;
`;

const HospitalImage = styled(Image)`
  border-radius: 8px;
  object-fit: cover;
`;

const TopWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0 24px;
`;

const HospitalWrapper = styled.div`
  overflow-x: hidden;
`;

const NameWrapper = styled.div`
  display: flex;
  align-items: center;

  & > h3 {
    ${({ theme }) => `
          ${theme.typography.UI17SB}
          color: ${theme.color.gray600};
      `}
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    word-break: break-all;
  }
`;

const IconInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 4px;
  gap: 4px;
`;

const InfoWrapper = styled.div`
  display: flex;
  white-space: nowrap;
  align-items: center;
`;

const Score = styled.span`
  ${({ theme }) => `
    ${theme.typography.UI13M}
    color: ${theme.color.gray600};
  `}
  margin-left: 4px;
`;

const ReviewCount = styled.span`
  ${({ theme }) => `
    ${theme.typography.UI13M}
    color: ${theme.color.gray300};
  `}
  margin-left: 1px;
`;

const DistanceWrapper = styled(InfoWrapper)``;

const Address = styled.span`
  ${({ theme }) => `
        ${theme.typography.UI13M}
        color: ${theme.color.gray300};
    `}
  flex: 1;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  word-break: break-all;
  margin-left: 4px;
`;

const BadgeWrapper = styled.div<{ hasItem: boolean }>`
  display: ${({ hasItem }) => (hasItem ? 'flex' : 'none')};
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  margin-top: 12px;
  padding-left: 24px;
  padding-right: 24px;
`;

const Badge = styled.div`
  ${({ theme }) => `
      ${theme.typography.UI12M}
      color: ${theme.color.gray300};
      background: ${theme.color.gray30};
      `}
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1px 4px;
  border-radius: 4px;
  height: 20px;
  margin-right: 6px;
`;

const NonF2fBadge = styled(Badge)`
  gap: 6px;
  ${({ theme }) => `
      ${theme.typography.UI12M}
      color: ${theme.color.primary600};
      background: ${theme.color.primary100};
      `}
`;

const RepresentativeDepartment = styled.div`
  display: flex;
  align-items: center;
  margin-top: 4px;
`;

const DepartmentName = styled.span`
  ${({ theme }) => theme.typography.UI12R};
  color: ${({ theme }) => theme.color.gray400};
`;

const DiagnosisTimeWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-top: 4px;
`;

const Stick = styled.div`
  width: 1px;
  height: 13px;
  background-color: ${({ theme }) => theme.color.gray100};
  margin-left: 8px;
  margin-right: 8px;
`;

const OperationDisplayTime = styled.span`
  ${({ theme }) => css`
    ${theme.typography.UI13R}
    color: ${theme.color.gray400};
  `}
`;

const AdBanners = styled.div`
  margin-top: 21px;
  display: flex;
  align-items: center;
  overflow-x: scroll;
  white-space: nowrap;
  padding-left: 24px;
  padding-right: 24px;
`;

const AdBanner = styled.div<{ isSingle: boolean }>`
  background-color: ${({ theme }) => theme.color.gray40};
  border-radius: 12px;
  padding: 16px;
  display: flex;
  align-items: center;
  height: 80px;

  margin-right: ${({ isSingle }) => (isSingle ? 0 : 12)}px;
  ${({ theme, isSingle }) => {
    const { client } = theme;
    if (client.isMobile) {
      return css`
        width: ${isSingle ? 'calc(100% - 24px)' : 'calc(100% - 36px)'};
        min-width: ${isSingle ? 'calc(100% - 24px)' : 'calc(100% - 36px)'};
      `;
    }
    return css`
      width: ${isSingle ? 'calc(100% - 24px)' : '520px'};
      min-width: ${isSingle ? 'calc(100% - 24px)' : '520px'};
    `;
  }}
`;

const AdContent = styled.div`
  margin-left: 12px;
`;

const AdContentTitle = styled.div`
  ${({ theme }) => theme.typography.UI14M};
  color: ${({ theme }) => theme.color.gray500};
`;

const PriceWrapper = styled.div`
  display: flex;
  margin-top: 2px;
`;

const DiscountPercent = styled.div`
  color: ${({ theme }) => theme.color.primary800};
  ${({ theme }) => theme.typography.UI16B};
  margin-right: 2px;
`;

const Price = styled.div`
  color: ${({ theme }) => theme.color.gray800};
  ${({ theme }) => theme.typography.UI16B};
`;

const HoveredMoreIcon = styled.div<{ isHovered: boolean }>`
  ${({ theme, isHovered }) => {
    const { client } = theme;
    if (client.isMobile) {
      return css`
        display: none;
      `;
    }
    return css`
      display: ${isHovered ? 'flex' : 'none'};
    `;
  }}
  position: absolute;
  right: 0;
  bottom: 20px;
  justify-content: center;
  align-items: center;
  background-color: transparent;
`;

const MoreWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const MoreText = styled.p`
  white-space: nowrap;
  ${({ theme }) => theme.typography.UI14M};
  color: ${({ theme }) => theme.color.gray500};
  margin-top: 2px;
`;

const InnerDivider = styled(Divider)`
  width: calc(100% - 48px);
`;

const DividerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-content: center;
`;
